import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function AboutPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const values = [
    {
      icon: 'ri-tools-line',
      title: 'Quality First',
      description: 'We never cut corners. Every professional on Taskbudy is expected to meet high standards of workmanship, safety, and care — because quality builds trust.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Trust & Transparency',
      description: 'From clear pricing to verified professionals, we believe trust is earned through honesty, accountability, and consistency.'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Speed Without Compromise',
      description: 'Life doesn\'t wait — and neither should help. We deliver fast, same-day solutions without sacrificing quality.'
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Respect for Homes & People',
      description: 'Every job is carried out with respect for customers\' homes, time, and peace of mind.'
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Fairness for Professionals',
      description: 'Great tradespeople deserve fair opportunities. We remove unnecessary bidding, hidden fees, and wasted time so professionals can focus on what they do best.'
    },
    {
      icon: 'ri-community-line',
      title: 'Community & Reputation',
      description: 'We\'re building more than a platform — we\'re building a service people proudly recommend to friends and family, strengthening local communities through reliable work.'
    }
  ];

  const team = [
    {
      name: 'Sarah Johnson',
      role: 'CEO & Founder',
      image: 'https://public.readdy.ai/ai/img_res/5efd0ceb9b2a3ffc5ea015b407ba5012.jpg',
      bio: 'With 15 years in the service industry, Sarah founded Taskbudy to revolutionize how people access home services.'
    },
    {
      name: 'Michael Chen',
      role: 'Head of Operations',
      image: 'https://readdy.ai/api/search-image?query=Professional%20male%20operations%20manager%20in%20modern%20office%2C%20confident%20business%20professional%20wearing%20professional%20attire%2C%20bright%20office%20background%2C%20executive%20portrait%2C%20simple%20clean%20background%20highlighting%20the%20person%2C%20professional%20corporate%20atmosphere&width=400&height=500&seq=team-ops-001&orientation=portrait',
      bio: 'Michael ensures our platform runs smoothly, managing our network of professionals and maintaining service quality.'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Customer Success Lead',
      image: 'https://readdy.ai/api/search-image?query=Professional%20female%20customer%20success%20manager%20in%20modern%20office%2C%20friendly%20business%20professional%20wearing%20professional%20attire%2C%20bright%20office%20background%2C%20executive%20portrait%2C%20simple%20clean%20background%20highlighting%20the%20person%2C%20professional%20corporate%20atmosphere&width=400&height=500&seq=team-cs-001&orientation=portrait',
      bio: 'Emily leads our customer support team, ensuring every customer has an exceptional experience with Taskbudy.'
    }
  ];

  return (
    <div className="min-h-screen bg-white w-full">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-md`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/e0dadf219d3b341a9184e92001cfe9b6.png" 
                alt="Taskbudy" 
                className="h-8 lg:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Home
              </Link>
              <Link to="/services" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Services
              </Link>
              <Link to="/locations" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Locations
              </Link>
              <Link to="/pricing" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Pricing
              </Link>
              <Link to="/about" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                About
              </Link>
              <Link to="/contact" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Contact
              </Link>
              <Link to="/auth/signin" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Sign In
              </Link>
              <Link to="/auth/signup" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Sign Up
              </Link>
              <Link
                to="/provider-signup"
                className="bg-[#fe7a14] hover:bg-[#e56a0f] text-white px-6 py-3 rounded-xl font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Become a Provider
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-[#0F2A44] w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="fixed top-[58px] left-0 right-0 bg-white shadow-lg z-40 border-t border-gray-200 animate-slideDown">
              <div className="flex flex-col py-2 max-h-[calc(100vh-58px)] overflow-y-auto">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Home
                </Link>
                <Link
                  to="/services"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Services
                </Link>
                <Link
                  to="/locations"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Locations
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Pricing
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  About
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Contact
                </Link>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  to="/auth/signin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Sign In
                </Link>
                <Link
                  to="/auth/signup"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Sign Up
                </Link>
                <Link
                  to="/provider-signup"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mx-4 my-2 px-4 py-3 bg-gradient-to-r from-[#fe7a14] to-[#ff8c3a] text-white font-bold rounded-lg text-center hover:from-[#e56a0f] hover:to-[#fe7a14] transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105 shadow-md"
                >
                  Become a Provider
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 sm:pb-16 bg-gradient-to-br from-[#0B3C5D] to-[#328BA8] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 animate-[fadeInUp_0.8s_ease-out]">
              About Taskbudy
            </h1>
            <p className="text-base sm:text-lg text-white/90 leading-relaxed animate-[fadeInUp_1s_ease-out] px-4">
              Taskbudy is a same-day home services platform connecting customers with trusted, verified local professionals. Built by a tradesperson with decades of experience, we focus on quality work, transparent pricing, and fast, reliable service.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="animate-[fadeInLeft_1s_ease-out]">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0B3C5D] mb-4 sm:mb-6">Our Story</h2>
              <div className="space-y-3 sm:space-y-4 text-sm sm:text-base text-gray-600 leading-relaxed">
                <p className="font-semibold text-gray-800">
                  Taskbudy didn't start as a tech idea — it started on building sites.
                </p>
                <p>
                  In 1995, our founder began his career as a builder. Over time, he expanded his skills into plumbing and electrical work, becoming a true multi-skilled tradesperson. Years on the tools taught him something the industry often forgets: <strong className="text-gray-800">quality always comes before money.</strong>
                </p>
                <p>
                  Turning up when you say you will. Doing the job properly the first time. Treating people's homes with respect. These weren't extras — they were standards.
                </p>
                <p>
                  But as the years went on, one frustration never disappeared.<br />
                  For customers, getting a reliable tradesperson quickly was stressful, slow, and uncertain.<br />
                  For skilled professionals, finding fair, consistent work meant bidding, quoting, paying fees, and wasting time — with no guarantees.
                </p>
                <p className="font-semibold text-gray-800">
                  It felt broken.
                </p>
                <p className="text-[#0B3C5D] font-semibold text-lg">
                  Taskbudy was created to fix that.
                </p>
                <p>
                  Built by someone who understands the trade from the ground up, Taskbudy removes friction on both sides. We make it easy for customers to get trusted help when they need it, and we make it fair and straightforward for professionals to do the work they love — without chasing jobs or cutting corners.
                </p>
                <p>
                  Our platform connects people with verified, quality-driven local professionals, offers transparent pricing, and focuses on fast, dependable service — including same-day solutions.
                </p>
                <p className="font-semibold text-gray-800 text-lg">
                  At its core, Taskbudy is about trust.
                </p>
                <p>
                  We're building a service people feel confident using, proud to recommend, and happy to come back to — knowing the job will be done right, every time.
                </p>
                <p className="italic text-gray-700 font-medium">
                  Because good work should never be hard to find.
                </p>
              </div>
            </div>
            <div className="relative animate-[fadeInRight_1s_ease-out]">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20professional%20team%20working%20together%20in%20bright%20office%20space%2C%20diverse%20group%20collaborating%20on%20home%20services%20platform%2C%20laptops%20and%20planning%20materials%20visible%2C%20clean%20contemporary%20workspace%20with%20natural%20lighting%2C%20simple%20background%20highlighting%20teamwork%20and%20innovation&width=800&height=600&seq=about-story-001&orientation=landscape"
                alt="Our Story"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-[#FF6A00] to-[#FF6A00]/80 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0B3C5D] mb-3 sm:mb-4">Our Values</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              The principles that guide everything we do at Taskbudy
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#FF6A00] to-[#FF8533] rounded-xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
                  <i className={`${value.icon} text-xl sm:text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#0B3C5D] mb-2 sm:mb-3">{value.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-[#0B3C5D] to-[#0B3C5D]/90 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
            Ready to Experience Quality Service?
          </h2>
          <p className="text-lg sm:text-xl mb-6 sm:mb-8 text-white/90 px-4">
            Join thousands of satisfied customers who trust Taskbudy for their home service needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-[#FF6A00] text-white rounded-lg font-semibold hover:bg-[#FF6A00]/90 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book a Service
            </button>
            <button
              onClick={() => navigate('/provider-signup')}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-[#0B3C5D] rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Become a Provider
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
